import * as THREE from 'three';
import { makeRng } from '../core/Rand.js';
import { ATMO } from './lightshaders/atmosphere.glsl.js';
import { SKY_VERT, SKY_BAKE_FRAG, SKY_DOME_FRAG } from './lightshaders/skyShaders.js';
import { installShaderPatches, sharedUniforms } from './lightshaders/inject.js';

/**
 * Sky — physically based atmosphere, volumetric cloud shell, aerial perspective.
 * Owner: lighting.
 *
 * The atmosphere is a single-scattering Rayleigh + Mie + ozone integral driven by
 * the extraterrestrial solar illuminance (128 000 lx), so everything downstream is
 * in real photometric units:
 *
 *   golden-hour sun, perpendicular      ~ 55 000-70 000 lx
 *   golden-hour sky, horizontal         ~  4 000-9 000 lx
 *   sunlit 18 % surface                 ~  3 500 nits
 *   sky-lit 18 % surface in shadow      ~    400 nits
 *
 * ~9:1 key-to-fill with a hard warm/cool split. That ratio is the whole reason a
 * filmic tonemap reads as film instead of as a washed-out render.
 *
 * The atmosphere + clouds are baked into an HDR cube once per sun change; the
 * visible dome samples it and adds the analytic limb-darkened sun disc, so the
 * per-frame cost is a single textured box.
 */

const DEG = Math.PI / 180;

/**
 * THE scene unit conversion. There is exactly one, and it lives here.
 * (Written up in CONTRACTS.md :: Scene units — read that first.)
 *
 * Everything in this module is computed in real photometric units — lux for
 * illuminance, cd/m² (nits) for radiance. The renderer's HDR buffer works in a
 * normalised range, so one conversion happens at the boundary:
 *
 *     1.0 scene unit == SCENE_UNIT_NITS nits
 *
 * Every light in the game — world *and* viewmodel — is authored against it.
 *
 * ### This number no longer carries the exposure calibration
 *
 * It used to: `SCENE_UNIT_NITS = 1 / derived.exposure(TOD.golden)`, so that the
 * published anchor read exactly 1.00 outdoors at golden hour. That derivation
 * had two problems, both measured (FINDINGS F1 STATUS: NOT CLOSED):
 *
 *  1. It was fitted **outdoors only**, so it said nothing about an interior.
 *     Measured, the auto-exposure wanted 0.26 on `level_rooftop` and 4.64 on
 *     `level_alley` — 4.15 stops apart — against a static anchor of 1.0. Four of
 *     six shots sat on the +/-1.5 stop clamp.
 *  2. It is a *unit convention*. Since the anchor is now applied to the frame as
 *     a pre-exposure (see `Lighting.applyExposure` and `PostFX`), scaling this
 *     constant scales the anchor by exactly the same factor and cancels: it
 *     cannot move the image, and therefore cannot have been the calibration.
 *
 * What it still decides is fp16 headroom and the meaning of every hand-authored
 * absolute in the build (FX flashes, tracer and reticle colours, ProbeRig
 * emissives — none of which route through `unitScale`). Changing it would move
 * all of those relative to the light rig for no gain, so it stays where it is.
 *
 * The exposure calibration now lives in two measured numbers below —
 * `KEY_RESPONSE` and `EXPOSURE_ADAPT` — and is derived against the interior and
 * exterior extremes together.
 *
 * Only the absolute scale is set here; every ratio (sun:sky:bounce:practical)
 * stays exactly physical, which is the part the tonemapper reacts to. The
 * physical values remain readable on `ctx.sky.derived`, and `ctx.sky.unitScale`
 * / `ctx.sky.nitsPerUnit` convert back.
 */
export const SCENE_UNIT_NITS = 5600;
export const UNIT_SCALE = 1 / SCENE_UNIT_NITS;

/* ------------------------------------------------------------------ *
 * The exposure anchor — one policy, used for every shot
 * ------------------------------------------------------------------ */

/**
 * The linear scene luminance the post pipeline meters to. This is the AE's
 * `key`: what the metering window averages to before the tonemapper.
 *
 * Re-derived from 0.17 when the cloud deck was corrected (FINDINGS F13). The
 * metering window is a percentile band over the whole frame, sky included, so
 * making the deck physically bright raised the metered average of every exterior
 * shot by ~37% — and auto-exposure answered by stopping down 0.45 stops. Left
 * alone, a correct sky would have been paid for out of the ground: measured on
 * `level_square`, median sRGB 0.345 -> 0.306 and p05 0.081 -> 0.077.
 *
 * 0.225 is the value that holds the hero exteriors at the median they already
 * had (0.34-0.36 on level_square and light_goldenhour), so the sky correction
 * shows up as highlights instead of as a darker frame.
 *
 * This does not touch the acceptance measurement: the histogram residual is
 * `predicted / actual` and the key cancels out of it.
 */
export const EXPOSURE_KEY = 0.225;

/**
 * How much of a scene's own brightness survives onto the screen.
 *
 * 1.0 = full adaptation: every shot is metered to `EXPOSURE_KEY` and a lantern-lit
 * cellar prints exactly as bright as a sunlit street. 0.0 = none: the street
 * blows out or the cellar goes black. Film and eyes sit in between and so does
 * this: at 0.82, a scene 10x darker than the reference prints 10^0.18 = 1.51x
 * darker on screen, so an interior still reads as an interior.
 *
 * Bounded from both directions by measurement, not taste:
 *
 *  - **Above**: 0.90 was tried and rejected on the frames. `level_alley` and
 *    `level_interior` came out at exposure 3.8 and 3.7 and read washed — pale,
 *    low-contrast, the milky look FINDINGS F1 is about. Near-full adaptation
 *    normalises every shot to the same key and deletes the value structure
 *    between a sunlit square and a shuttered room.
 *  - **Below**: `level_interior` sits at 271 nits against a 3205 nit reference,
 *    and was railed at exposure 2.828 before this work. Holding it at or above
 *    that — so the bounce-light result (p05 0.1272) cannot regress — needs
 *    1 - ADAPT <= 0.21. 0.82 lands the interior at 3.05 and the alley at 3.05,
 *    ~8% above the floor.
 *
 * Nothing about the acceptance test depends on this number: the residual the
 * histogram settles on is `predicted / actual` and cancels the display key
 * entirely, so this is free to be retuned as an art decision.
 */
export const EXPOSURE_ADAPT = 0.82;

/**
 * Metered frame luminance per unit of local irradiance — i.e. what fraction of
 * the light arriving at the camera comes back as the number the histogram
 * meters.
 *
 * The naive answer is the 18% grey card, `0.18 / PI` = 0.0573. Measured against
 * the six acceptance shots it is 0.0353, because a frame is not a grey card
 * facing the light: most of what is on screen is vertical, shadowed, or GTAO'd,
 * and the metering window is the 45th-96th percentile rather than the mean.
 * Fitting that ratio jointly over `level_square`, `level_alley`,
 * `level_interior`, `level_rooftop`, `light_goldenhour` and `wpn_rifle_hip`
 * (interior AND exterior extremes, which is the part the old calibration
 * skipped) leaves a residual spread of 1.06 stops — that residual is framing,
 * and it is what `tools/exposure-check.mjs` now measures.
 *
 * Re-derive it by running the tool and multiplying by the geometric mean of the
 * settled EVs: the EV *is* predicted/actual, so it is its own correction factor.
 * (0.6166 was the first fit; 0.784 is that fit re-centred after the cloud deck
 * was corrected and every exterior frame's metered luminance moved.)
 *
 * Changing this number cannot change the image. The anchor is `displayKey / K`
 * and the residual is `K / actual`, so K cancels in their product; it only moves
 * where the acceptance measurement reads 1.0. That is the point of it.
 */
export const KEY_RESPONSE = 0.18 / Math.PI * 0.784;

/**
 * The display key a scene of `keyNits` should print at, given a reference
 * scene of `refNits`. Partial adaptation, per EXPOSURE_ADAPT.
 */
export function displayKeyFor(keyNits, refNits) {
  const r = Math.max(1e-6, keyNits / Math.max(1e-6, refNits));
  return EXPOSURE_KEY * Math.pow(r, 1 - EXPOSURE_ADAPT);
}

/** Metered luminance (scene units) expected from `irradiance` scene units. */
export function keyLuminanceFor(irradiance) {
  return KEY_RESPONSE * Math.max(0, irradiance);
}

/* ------------------------------------------------------------------ *
 * JS mirror of the GLSL atmosphere. Same constants, fewer steps.
 * ------------------------------------------------------------------ */

const { Rg, Rt, Hr, Hm, betaR, betaM, betaMext, betaO, ozoneCenter, ozoneWidth, sunIlluminanceTOA } = ATMO;

const smoothstep = (e0, e1, x) => {
  const t = Math.min(1, Math.max(0, (x - e0) / (e1 - e0)));
  return t * t * (3 - 2 * t);
};

function densities(h, out) {
  out[0] = Math.exp(-h / Hr);
  out[1] = Math.exp(-h / Hm);
  out[2] = Math.max(0, 1 - Math.abs(h - ozoneCenter) / ozoneWidth);
  return out;
}

/** Far intersection with a sphere of radius r centred at the origin. */
function raySphereFar(oy, dx, dy, dz, r) {
  const b = oy * dy;
  const c = oy * oy - r * r;
  const disc = b * b - c;
  if (disc < 0) return 0;
  return Math.max(0, -b + Math.sqrt(disc));
}

function raySphereNear(oy, dx, dy, dz, r) {
  const b = oy * dy;
  const c = oy * oy - r * r;
  const disc = b * b - c;
  if (disc < 0) return -1;
  const s = Math.sqrt(disc);
  const t0 = -b - s;
  const t1 = -b + s;
  if (t1 < 0) return -1;
  return t0 < 0 ? t1 : t0;
}

const _d = [0, 0, 0];

/**
 * Optical depth (Rayleigh, Mie, ozone) from `altitude` along dir out to space.
 * Warped stepping (t ~ u^3) — with uniform steps a 250 km slant path resolves
 * barely 15 % of the Mie term and the low sun never goes warm.
 */
function opticalDepthToSpace(altitude, dx, dy, dz, steps = 12) {
  const oy = Rg + altitude;
  const T = raySphereFar(oy, dx, dy, dz, Rt);
  let a = 0, b = 0, c = 0;
  let prev = 0;
  for (let i = 0; i < steps; i++) {
    const tb = T * Math.pow((i + 1) / steps, 3);
    const ds = tb - prev;
    const tm = 0.5 * (prev + tb);
    prev = tb;
    if (ds <= 0) continue;
    const px = dx * tm, py = oy + dy * tm, pz = dz * tm;
    const h = Math.max(0, Math.sqrt(px * px + py * py + pz * pz) - Rg);
    const dR = Math.exp(-h / Hr);
    const dM = Math.exp(-h / Hm);
    const dO = Math.max(0, 1 - Math.abs(h - ozoneCenter) / ozoneWidth);
    a += dR * ds; b += dM * ds; c += dO * ds;
  }
  return [a, b, c];
}

function extinction(od, turbidity, ozone, out) {
  for (let i = 0; i < 3; i++) {
    out[i] = Math.exp(-(betaR[i] * od[0] + betaMext * turbidity * od[1] + betaO[i] * ozone * od[2]));
  }
  return out;
}

const phaseRayleigh = (mu) => (3 / (16 * Math.PI)) * (1 + mu * mu);

function phaseMie(mu, g) {
  const g2 = g * g;
  const num = 3 * (1 - g2) * (1 + mu * mu);
  const den = 8 * Math.PI * (2 + g2) * Math.pow(Math.max(1e-4, 1 + g2 - 2 * g * mu), 1.5);
  return num / den;
}

const _t3 = [0, 0, 0];
const _ext = [0, 0, 0];

/* ---- transmittance LUT ------------------------------------------- */

const LUT_W = ATMO.lutWidth;
const LUT_H = ATMO.lutHeight;

/**
 * Transmittance from (altitude, vertical cosine) to space, baked once per
 * atmosphere change. Sampled by both the GLSL bake and the JS mirror, so the
 * directional light and the sky can never disagree.
 */
function buildTransmittanceLUT(turbidity, ozone) {
  const data = new Float32Array(LUT_W * LUT_H * 4);
  for (let j = 0; j < LUT_H; j++) {
    const v = j / (LUT_H - 1);
    const h = v * v * ATMO.lutMaxAltitude;
    for (let i = 0; i < LUT_W; i++) {
      const mu = ATMO.lutMuMin + (i / (LUT_W - 1)) * ATMO.lutMuRange;
      const dy = mu;
      const dxz = Math.sqrt(Math.max(0, 1 - mu * mu));
      const od = opticalDepthToSpace(h, dxz, dy, 0, 24);
      extinction(od, turbidity, ozone, _t3);
      // Below the local horizon the planet blocks the sun; fade rather than clip
      // so dusk stays smooth instead of snapping off.
      const fade = smoothstep(-0.075, 0.02, mu);
      const o = (j * LUT_W + i) * 4;
      data[o] = _t3[0] * fade;
      data[o + 1] = _t3[1] * fade;
      data[o + 2] = _t3[2] * fade;
      data[o + 3] = 1;
    }
  }
  const tex = new THREE.DataTexture(data, LUT_W, LUT_H, THREE.RGBAFormat, THREE.FloatType);
  tex.name = 'atmosphereTransmittance';
  tex.minFilter = THREE.LinearFilter;
  tex.magFilter = THREE.LinearFilter;
  tex.wrapS = tex.wrapT = THREE.ClampToEdgeWrapping;
  tex.colorSpace = THREE.NoColorSpace;
  tex.generateMipmaps = false;
  tex.needsUpdate = true;
  return { texture: tex, data };
}

function sampleLUT(lut, h, mu, out) {
  const v = Math.sqrt(THREE.MathUtils.clamp(h / ATMO.lutMaxAltitude, 0, 1)) * (LUT_H - 1);
  const u = THREE.MathUtils.clamp((mu - ATMO.lutMuMin) / ATMO.lutMuRange, 0, 1) * (LUT_W - 1);
  const j0 = Math.floor(v), i0 = Math.floor(u);
  const j1 = Math.min(LUT_H - 1, j0 + 1), i1 = Math.min(LUT_W - 1, i0 + 1);
  const fv = v - j0, fu = u - i0;
  const d = lut.data;
  for (let k = 0; k < 3; k++) {
    const a = d[(j0 * LUT_W + i0) * 4 + k] * (1 - fu) + d[(j0 * LUT_W + i1) * 4 + k] * fu;
    const b = d[(j1 * LUT_W + i0) * 4 + k] * (1 - fu) + d[(j1 * LUT_W + i1) * 4 + k] * fu;
    out[k] = a * (1 - fv) + b * fv;
  }
  return out;
}

/** Sky radiance (nits) along dir from `altitude`. Mirrors aaaSkyRadiance. */
function skyRadianceJS(lut, dx, dy, dz, sun, turbidity, ozone, mieG, altitude, out) {
  const oy = Rg + altitude;
  const tTop = raySphereFar(oy, dx, dy, dz, Rt);
  const tGround = raySphereNear(oy, dx, dy, dz, Rg);
  const hitsGround = tGround > 0;
  const tMax = hitsGround ? tGround : tTop;

  const N = 16;
  const WARP = 2.5;
  let odA = 0, odB = 0, odC = 0;
  const sr = [0, 0, 0];
  const sm = [0, 0, 0];

  let prev = 0;
  for (let i = 0; i < N; i++) {
    const tb = tMax * Math.pow((i + 1) / N, WARP);
    const ds = tb - prev;
    const tm = 0.5 * (prev + tb);
    prev = tb;
    if (ds <= 0) continue;

    const px = dx * tm, py = oy + dy * tm, pz = dz * tm;
    const len = Math.sqrt(px * px + py * py + pz * pz);
    const h = Math.max(0, len - Rg);
    const dR = Math.exp(-h / Hr);
    const dM = Math.exp(-h / Hm);
    const dO = Math.max(0, 1 - Math.abs(h - ozoneCenter) / ozoneWidth);
    odA += dR * ds; odB += dM * ds; odC += dO * ds;

    _d[0] = odA; _d[1] = odB; _d[2] = odC;
    extinction(_d, turbidity, ozone, _ext);

    const cosSun = (px * sun.x + py * sun.y + pz * sun.z) / len;
    sampleLUT(lut, h, cosSun, _t3);

    const w = ds;
    sr[0] += _ext[0] * _t3[0] * dR * w;
    sr[1] += _ext[1] * _t3[1] * dR * w;
    sr[2] += _ext[2] * _t3[2] * dR * w;
    sm[0] += _ext[0] * _t3[0] * dM * w;
    sm[1] += _ext[1] * _t3[1] * dM * w;
    sm[2] += _ext[2] * _t3[2] * dM * w;
  }

  const mu = dx * sun.x + dy * sun.y + dz * sun.z;
  const pr = phaseRayleigh(mu);
  const pm = phaseMie(mu, mieG);
  for (let i = 0; i < 3; i++) {
    out[i] = sunIlluminanceTOA * (betaR[i] * sr[i] * pr + betaM * turbidity * sm[i] * pm)
           + sunIlluminanceTOA * betaR[i] * sr[i] * 0.34 / (4 * Math.PI);
  }
  return out;
}

/* ------------------------------------------------------------------ *
 * Time of day -> sun direction. Real solar geometry so golden hour has
 * the right azimuth sweep and the shadows genuinely rake.
 * ------------------------------------------------------------------ */

const LATITUDE = 38 * DEG;
const DECLINATION = 12 * DEG;

function sunFromTime(t) {
  const H = (t * 24 - 12) * 15 * DEG;
  const sinElev = Math.sin(LATITUDE) * Math.sin(DECLINATION)
                + Math.cos(LATITUDE) * Math.cos(DECLINATION) * Math.cos(H);
  const elev = Math.asin(Math.max(-1, Math.min(1, sinElev)));
  const cosElev = Math.cos(elev);
  let cosA = (Math.sin(DECLINATION) - sinElev * Math.sin(LATITUDE)) / Math.max(1e-4, cosElev * Math.cos(LATITUDE));
  cosA = Math.max(-1, Math.min(1, cosA));
  let azim = Math.acos(cosA);          // from north, 0..pi
  if (H > 0) azim = 2 * Math.PI - azim; // afternoon -> west
  return { elev, azim };
}

function dirFromElevAzim(elev, azim, out) {
  const ce = Math.cos(elev);
  return out.set(Math.sin(azim) * ce, Math.sin(elev), -Math.cos(azim) * ce).normalize();
}

/* ------------------------------------------------------------------ */

/**
 * `golden` is the reference time of day: every hero shot uses it and
 * SCENE_UNIT_NITS is calibrated at it.
 *
 * It used to be 0.7194 — a 16 deg sun. That is a beautiful *skyline* and a
 * broken *map*: in a dense urban block a 16 deg sun puts every playable surface
 * in shadow (FINDINGS F1b), and no amount of exposure recovers a play space
 * that receives no direct light. 0.6555 puts the sun at 34 deg / azimuth 258
 * deg, i.e. raking across the map's three lanes from the west:
 *
 *   - the market square floor takes direct sun (N.L = 0.56),
 *   - the souk house's west facade — the right flank, the one with the windows
 *     cut onto the square — is fully lit, and the sun enters its shopfront, so
 *     the interior god rays have something to be made of,
 *   - the left alley stays in shadow, which is a gameplay affordance: a lit
 *     side to fight over and a dark side to hold.
 *
 * 34 deg still carries the warm/cool split that makes golden hour read (the key
 * is ~4700 K against a ~11 000 K sky fill); the low sun was never what produced
 * that, the atmospheric path length was.
 */
export const TOD = {
  dawn: 0.248,
  morning: 0.36,
  noon: 0.5,
  golden: 0.6555,   // sun elevation ~34 deg, azimuth ~258 deg — see above
  dusk: 0.776,
};

export function createSkySystem() {
  const rng = makeRng(0x5c1b1e);

  const state = {
    timeOfDay: TOD.golden,
    turbidity: 4.0,
    ozone: 1.15,
    mieG: 0.76,
    groundAlbedo: new THREE.Color(0.16, 0.14, 0.12),
    cloudCoverage: 0.58,
    cloudDensity: 1.15,
    cloudBase: 1500,
    cloudTop: 3600,
    cloudScale: 3.6,   // base noise ~2.5 km per cell — at 1.0 the whole visible
                       // sky fell inside a single cell and either had clouds or
                       // did not, with no shape in between
    cloudDetail: 1.0,
    altitude: 40,
  };

  const sunDirection = new THREE.Vector3(0, 1, 0);
  const sunColor = new THREE.Color(1, 1, 1);
  const skyColor = new THREE.Color(0.4, 0.55, 0.8);
  const groundBounceColor = new THREE.Color(0.3, 0.25, 0.2);
  const zenithColor = new THREE.Color(0.3, 0.45, 0.75);

  let cubeRT = null;
  let cubeCamera = null;
  let bakeScene = null;
  let bakeMat = null;
  let domeMat = null;
  let dome = null;
  let ctxRef = null;
  let dirty = true;
  let cubeSize = 1024;
  let lut = null;

  function ensureLUT(force) {
    if (lut && !force) return lut;
    lut?.texture.dispose();
    lut = buildTransmittanceLUT(state.turbidity, state.ozone);
    bakeUniforms.uTransmittanceLUT.value = lut.texture;
    return lut;
  }

  const bakeUniforms = {
    uTurbidity: { value: state.turbidity },
    uOzone: { value: state.ozone },
    uGroundAlbedo: { value: new THREE.Vector3(0.16, 0.14, 0.12) },
    uSunDirection: { value: sunDirection },
    uMieG: { value: state.mieG },
    uTransmittanceLUT: { value: null },
    uCloudBase: { value: state.cloudBase },
    uCloudTop: { value: state.cloudTop },
    uCloudCoverage: { value: state.cloudCoverage },
    uCloudDensity: { value: state.cloudDensity },
    uCloudScale: { value: state.cloudScale },
    uCloudWind: { value: new THREE.Vector2(0, 0) },
    uCloudDetail: { value: state.cloudDetail },
    uCloudSteps: { value: 64 },
    uAltitude: { value: state.altitude },
    uSkyAmbient: { value: new THREE.Vector3(1000, 1400, 2200) },
    uCloudSunRadiance: { value: new THREE.Vector3(60000, 55000, 45000) },
    /* Overflow guard on baked sky radiance, nothing more. It used to be 12 000
       nits — a de-rating of a sunlit cumulus (~30 000 nits) invented to work
       around an auto-exposure floor that could not stop down past 0.12. The
       floor is gone (PostFX anchors and clamps around the calibrated exposure),
       so the physical value stands. The only remaining job is keeping the half
       float cube out of trouble: 120 000 nits / SCENE_UNIT_NITS is ~63 units,
       comfortably inside fp16 with room for the PMREM's blur to add. */
    uExposureGuard: { value: 120000 },
    uUnitScale: { value: UNIT_SCALE },
  };

  const domeUniforms = {
    uSkyCube: { value: null },
    uSunDirection: { value: sunDirection },
    uSunDiscRadiance: { value: new THREE.Vector3(1, 1, 1) },
    uSunAngularRadius: { value: ATMO.sunAngularRadius },
    uSkyIntensity: { value: 1 },
    // One cube texel in radians; set from the real cube size at init. The dome
    // filters across it to kill the 1-px cloud cutout (see SKY_DOME_FRAG).
    uTexelAngle: { value: (Math.PI / 2) / 1024 },
  };

  /* --- derived lighting quantities ------------------------------- */

  const derived = {
    sunIlluminance: new THREE.Vector3(),   // lx, perpendicular to the sun
    sunLux: 0,                             // luminance-weighted magnitude
    skyIlluminance: new THREE.Vector3(),   // lx on an up-facing surface
    skyLux: 0,
    avgSkyRadiance: new THREE.Vector3(),
    horizonRadiance: new THREE.Vector3(),
    zenithRadiance: new THREE.Vector3(),
    groundBounce: new THREE.Vector3(),
    exposure: 1,
    exposureUnits: 1,
    /**
     * The metered luminance an open, sky-and-sun-lit point receives at this time
     * of day, in scene units. This is the *global* term of the exposure anchor;
     * Lighting.js scales it by what the camera can actually see (sun visibility
     * and the baked irradiance volume) to get the local one.
     */
    keyLuminance: 0,
    /** Same, captured once at TOD.golden — the reference the adaptation is relative to. */
    keyLuminanceRef: 0,
    openIrradiance: 0,      // sun + sky + ground bounce on an open point, scene units
    elevationDeg: 0,
    azimuthDeg: 0,
  };

  // Deterministic cosine-weighted hemisphere directions for the sky-illuminance
  // integral. Generated once, reused for every time of day.
  const hemiDirs = [];
  {
    const N = 96;
    for (let i = 0; i < N; i++) {
      const u = (i + 0.5) / N;
      const v = rng();
      const r = Math.sqrt(u);
      const phi = 2 * Math.PI * v;
      hemiDirs.push(new THREE.Vector3(r * Math.cos(phi), Math.sqrt(Math.max(0, 1 - u)), r * Math.sin(phi)));
    }
  }

  const _rad = [0, 0, 0];

  function recomputeDerived() {
    const { elev, azim } = sunFromTime(state.timeOfDay);
    dirFromElevAzim(elev, azim, sunDirection);
    derived.elevationDeg = elev / DEG;
    derived.azimuthDeg = azim / DEG;
    deriveFromSun();
  }

  /**
   * The adaptation reference, measured once at TOD.golden whatever time of day
   * the level starts at. Everything the exposure anchor does is relative to
   * this, so it must not drift with the sun — if it did, dawn and noon would
   * both meter to the same key and the partial adaptation would be a no-op.
   */
  function captureReference() {
    const prev = state.timeOfDay;
    state.timeOfDay = TOD.golden;
    recomputeDerived();
    derived.keyLuminanceRef = derived.keyLuminance;
    if (prev !== TOD.golden) {
      state.timeOfDay = prev;
      recomputeDerived();
    }
  }

  /** Everything downstream of `sunDirection`. Split out so an explicit sun
   *  angle (setSunAngles) derives exactly what a time of day does. */
  function deriveFromSun() {
    // --- direct sun (same LUT the sky shader samples, so they cannot drift)
    ensureLUT(false);
    sampleLUT(lut, 0, sunDirection.y, _t3);
    derived.sunIlluminance.set(
      sunIlluminanceTOA * _t3[0],
      sunIlluminanceTOA * _t3[1],
      sunIlluminanceTOA * _t3[2],
    );
    const lum = 0.2126 * derived.sunIlluminance.x + 0.7152 * derived.sunIlluminance.y + 0.0722 * derived.sunIlluminance.z;
    derived.sunLux = lum;
    if (lum > 1e-3) {
      sunColor.setRGB(
        derived.sunIlluminance.x / lum,
        derived.sunIlluminance.y / lum,
        derived.sunIlluminance.z / lum,
      );
    } else {
      sunColor.setRGB(1, 1, 1);
    }

    // --- hemispherical sky integral (cosine weighted -> irradiance directly)
    let ir = 0, ig = 0, ib = 0;   // illuminance
    let ar = 0, ag = 0, ab = 0;   // average radiance
    for (const d of hemiDirs) {
      skyRadianceJS(lut, d.x, d.y, d.z, sunDirection, state.turbidity, state.ozone, state.mieG, state.altitude, _rad);
      ir += _rad[0]; ig += _rad[1]; ib += _rad[2];
      ar += _rad[0]; ag += _rad[1]; ab += _rad[2];
    }
    // cosine-weighted MC estimator of integral(L cos dw) = pi * mean(L)
    const kI = Math.PI / hemiDirs.length;
    derived.skyIlluminance.set(ir * kI, ig * kI, ib * kI);
    derived.skyLux = 0.2126 * derived.skyIlluminance.x + 0.7152 * derived.skyIlluminance.y + 0.0722 * derived.skyIlluminance.z;
    derived.avgSkyRadiance.set(ar / hemiDirs.length, ag / hemiDirs.length, ab / hemiDirs.length);

    // --- reference directions for grading / fog
    skyRadianceJS(lut, 0, 1, 0, sunDirection, state.turbidity, state.ozone, state.mieG, state.altitude, _rad);
    derived.zenithRadiance.set(_rad[0], _rad[1], _rad[2]);
    // horizon away from the sun — the cool fill reference
    const hx = -sunDirection.x, hz = -sunDirection.z;
    const hl = Math.hypot(hx, hz) || 1;
    skyRadianceJS(lut, hx / hl * 0.985, 0.17, hz / hl * 0.985, sunDirection, state.turbidity, state.ozone, state.mieG, state.altitude, _rad);
    derived.horizonRadiance.set(_rad[0], _rad[1], _rad[2]);

    // --- ground bounce: one Lambert bounce of (sun + sky) off the ground albedo
    const cosSun = Math.max(0, sunDirection.y);
    derived.groundBounce.set(
      state.groundAlbedo.r * (derived.sunIlluminance.x * cosSun + derived.skyIlluminance.x),
      state.groundAlbedo.g * (derived.sunIlluminance.y * cosSun + derived.skyIlluminance.y),
      state.groundAlbedo.b * (derived.sunIlluminance.z * cosSun + derived.skyIlluminance.z),
    );

    // Normalised palette colours for the light rig.
    const norm = (v, out) => {
      const l = Math.max(1e-4, 0.2126 * v.x + 0.7152 * v.y + 0.0722 * v.z);
      out.setRGB(v.x / l, v.y / l, v.z / l);
      return l;
    };
    norm(derived.skyIlluminance, skyColor);
    norm(derived.groundBounce, groundBounceColor);
    norm(derived.zenithRadiance, zenithColor);

    // --- exposure. Anchored on a sunlit 18 % lambertian, with partial
    // adaptation so dawn/dusk stay moodier than noon instead of matching it.
    // `exposureUnits` is what the render team anchors its histogram to
    // (ctx.lighting.suggestedExposure). SCENE_UNIT_NITS is calibrated so that
    // at TOD.golden this comes out at 1.00 — if it drifts far from 1, either the
    // lighting or the calibration has moved and one of them is wrong.
    const keyLuminance = Math.max(20, (derived.sunLux * Math.max(0.12, cosSun) + derived.skyLux) * 0.18 / Math.PI);
    const reference = 3000;
    const adapted = reference * Math.pow(keyLuminance / reference, 0.82);
    derived.exposure = 0.62 / adapted;                    // physical: nits -> display
    derived.exposureUnits = derived.exposure / UNIT_SCALE; // for a scene already in render units

    /**
     * The anchor's global term. Irradiance arriving at an open point that can
     * see the whole sky and the sun: direct sun on a horizontal surface, the
     * sky hemisphere, and one ground bounce. Lighting.js multiplies the sun
     * part by what the camera can see and replaces the other two with the baked
     * irradiance volume, which is the only part that knows about a roof.
     */
    const bounceLum = 0.2126 * derived.groundBounce.x
                    + 0.7152 * derived.groundBounce.y
                    + 0.0722 * derived.groundBounce.z;
    derived.openIrradiance = (derived.sunLux * cosSun + derived.skyLux + bounceLum) * UNIT_SCALE;
    derived.keyLuminance = keyLuminanceFor(derived.openIrradiance);

    // --- cloud deck lighting (uniforms for the bake)
    sampleLUT(lut, state.cloudBase, sunDirection.y, _t3);
    bakeUniforms.uCloudSunRadiance.value.set(
      sunIlluminanceTOA * _t3[0],
      sunIlluminanceTOA * _t3[1],
      sunIlluminanceTOA * _t3[2],
    );
    bakeUniforms.uSkyAmbient.value.copy(derived.avgSkyRadiance);

    // Sun disc radiance, clamped so the HDR buffers never see an fp16 overflow.
    const solidAngle = 2 * Math.PI * (1 - Math.cos(ATMO.sunAngularRadius));
    const discScale = Math.min(1, 42000 / Math.max(1e-3, derived.sunLux / solidAngle));
    domeUniforms.uSunDiscRadiance.value.set(
      derived.sunIlluminance.x / solidAngle * discScale * UNIT_SCALE,
      derived.sunIlluminance.y / solidAngle * discScale * UNIT_SCALE,
      derived.sunIlluminance.z / solidAngle * discScale * UNIT_SCALE,
    );

    bakeUniforms.uTurbidity.value = state.turbidity;
    bakeUniforms.uOzone.value = state.ozone;
    bakeUniforms.uMieG.value = state.mieG;
    bakeUniforms.uGroundAlbedo.value.set(state.groundAlbedo.r, state.groundAlbedo.g, state.groundAlbedo.b);
    bakeUniforms.uCloudCoverage.value = state.cloudCoverage;
    bakeUniforms.uCloudDensity.value = state.cloudDensity;
    bakeUniforms.uCloudBase.value = state.cloudBase;
    bakeUniforms.uCloudTop.value = state.cloudTop;
    bakeUniforms.uCloudScale.value = state.cloudScale;
    bakeUniforms.uCloudDetail.value = state.cloudDetail;
    bakeUniforms.uAltitude.value = state.altitude;

    sharedUniforms.aaaSunDirection.value.copy(sunDirection);
    updateFog();
  }

  /* --- aerial perspective ---------------------------------------- */

  /**
   * Aerial perspective, parameterised by meteorological range.
   *
   * ### Why the old numbers produced none at all
   *
   * `density 0.0006 /m` at a **16 m scale height** is a ground-mist layer, not
   * an atmosphere: at the 51 m camera height of `level_overview` the height
   * integral cuts it to a quarter, and the only term that survives at altitude
   * was `uniformDensity 7e-5 /m`. Measured against the real geometry — the far
   * skyline in `level_overview` is 121 m out and the far block in
   * `level_rooftop` 140 m — that is an optical depth of **0.03**, i.e. 3 %
   * haze. Three percent is invisible, which is exactly what the review found:
   * "monochrome salmon from the near rooftop to the far skyline, zero aerial
   * perspective."
   *
   * The physics that was missing is that the aerosol which produces aerial
   * perspective is *boundary-layer* aerosol — it is mixed through the lowest
   * kilometre, not through the lowest 16 m. `hazeScaleHeight` is now 700 m, so
   * the whole playable volume sits inside it and a 121 m sightline actually
   * accumulates something.
   *
   * `visibility` is the meteorological range in metres: sigma_ext = 3.912 / V
   * (Koschmieder). 3200 m is *light dust haze* — a normal afternoon over a
   * desert city, and the visibility a Middle-Eastern CoD map is graded for. It
   * gives 0.049 optical depth at 40 m (the market square: 5 %, imperceptible),
   * 0.148 at 121 m and 0.171 at 140 m (the two far samples the review named:
   * 14-17 %, clearly readable). It cannot rebuild the F1 veil: that was two
   * stops of exposure lift on a whole frame, this is 5 % over the near field
   * and the in-scatter is gated by sky visibility on top (see inject.js).
   *
   * `residual` is the free-atmosphere Rayleigh + Mie floor that survives above
   * the boundary layer, so a rooftop still hazes toward a distant skyline.
   */
  const fogParams = {
    visibility: 3200,       // m, meteorological range at the reference height
    hazeScaleHeight: 700,   // m, boundary-layer mixing depth
    residual: 0.00012,      // 1/m, height-independent floor
    height: 0,              // m, where `visibility` is measured
    sunScatter: 0.035,
    sunG: 0.74,
    skyTint: new THREE.Color(1.0, 1.0, 1.0),
    maxOpacity: 0.94,
    skyLod: 4.0,
  };

  function updateFog() {
    const u = sharedUniforms;
    // Koschmieder: a 0.02 contrast threshold gives sigma_ext = ln(50) / V.
    const sigma = 3.912 / Math.max(50, fogParams.visibility);
    u.aaaFogParams.value.set(sigma, 1 / Math.max(1, fogParams.hazeScaleHeight), fogParams.residual, fogParams.skyLod);
    u.aaaFogHeight.value = fogParams.height;
    u.aaaFogSunG.value = fogParams.sunG;
    u.aaaFogMaxOpacity.value = fogParams.maxOpacity;
    u.aaaFogSkyTint.value.copy(fogParams.skyTint);
    const k = fogParams.sunScatter / Math.PI * UNIT_SCALE;
    u.aaaFogSunColor.value.setRGB(
      derived.sunIlluminance.x * k,
      derived.sunIlluminance.y * k,
      derived.sunIlluminance.z * k,
    );
  }

  /* --- cube bake -------------------------------------------------- */

  const timing = { bakeMs: 0, bakes: 0, deriveMs: 0 };

  function bake(renderer) {
    if (!cubeRT) return;
    const t0 = performance.now();
    const prevTarget = renderer.getRenderTarget();
    const prevActiveCube = renderer.getActiveCubeFace();
    cubeCamera.update(renderer, bakeScene);
    renderer.setRenderTarget(prevTarget, prevActiveCube);
    dirty = false;
    timing.bakeMs = performance.now() - t0;
    timing.bakes++;
  }

  const api = {
    // frozen surface
    sunDirection,
    sunColor,
    sunIntensity: 0,
    skyColor,
    cubeTexture: null,
    fogParams,
    uniforms: { bake: bakeUniforms, dome: domeUniforms, shared: sharedUniforms },

    // scene-unit conversion (see SCENE_UNIT_NITS)
    unitScale: UNIT_SCALE,
    nitsPerUnit: SCENE_UNIT_NITS,

    // extras other systems read
    derived,
    timing,
    state,
    zenithColor,
    groundBounceColor,
    TOD,

    setTimeOfDay(t) {
      const next = ((t % 1) + 1) % 1;
      if (Math.abs(next - state.timeOfDay) < 1e-6 && !dirty) return;
      state.timeOfDay = next;
      recomputeDerived();
      api.sunIntensity = derived.sunLux;
      dirty = true;
      if (ctxRef) bake(ctxRef.renderer);
      ctxRef?.events.emit('sky:changed', { timeOfDay: state.timeOfDay, sunDirection, sunIntensity: derived.sunLux });
    },

    /**
     * Sky radiance along `dir` in render units. Used by the irradiance-volume
     * bake so probes that can see out of a window pick up real daylight.
     */
    sampleRadiance(dir, out = new THREE.Vector3()) {
      ensureLUT(false);
      skyRadianceJS(lut, dir.x, dir.y, dir.z, sunDirection,
        state.turbidity, state.ozone, state.mieG, state.altitude, _rad);
      return out.set(_rad[0] * UNIT_SCALE, _rad[1] * UNIT_SCALE, _rad[2] * UNIT_SCALE);
    },

    /**
     * Explicit sun angles, bypassing the solar-geometry model. Re-derives every
     * dependent quantity — it used to move only `sunDirection`, so the light
     * rig, the fog and the exposure all kept the *previous* sun's magnitudes.
     */
    setSunAngles(elevationDeg, azimuthDeg) {
      dirFromElevAzim(elevationDeg * DEG, azimuthDeg * DEG, sunDirection);
      derived.elevationDeg = elevationDeg;
      derived.azimuthDeg = azimuthDeg;
      deriveFromSun();
      api.sunIntensity = derived.sunLux;
      dirty = true;
      if (ctxRef) {
        bake(ctxRef.renderer);
        ctxRef.events.emit('sky:changed', { timeOfDay: state.timeOfDay, sunDirection, sunIntensity: derived.sunLux });
      }
    },

    setWeather(opts = {}) {
      const atmoChanged = opts.turbidity !== undefined || opts.ozone !== undefined;
      Object.assign(state, opts);
      if (atmoChanged) ensureLUT(true);
      recomputeDerived();
      api.sunIntensity = derived.sunLux;
      dirty = true;
      if (ctxRef) bake(ctxRef.renderer);
    },

    markDirty() { dirty = true; },
    isDirty: () => dirty,
    bake: (renderer) => bake(renderer),
  };

  return {
    name: 'sky',
    priority: 30,

    init(ctx) {
      ctxRef = ctx;
      installShaderPatches();

      /**
       * Cube resolution, decided rather than inherited.
       *
       * 768/face is 8.5 texels/deg against ~26 px/deg on screen, and the review
       * called the cloud shell soft because of it. 1024 gives 11.4 texels/deg.
       * That still does not match the screen, and matching it would need a 2304
       * cube (340 MB, 9x the bake) — not worth it for a shell at 3 km. What
       * 1024 *does* buy is that the texel is now finer than the smallest real
       * feature in the field: the billow detail is ~100 m across, which at
       * 3 km subtends 1.9 deg = 22 texels. Below that threshold extra
       * resolution only sharpens quadrature error, so this is the right place
       * to stop. Cost is a one-off ~0.5 s bake per sun change; the per-frame
       * cost is unchanged (one textured box) and 67 MB of fp16 with mips.
       */
      cubeSize = ctx.quality === 'low' ? 256 : ctx.quality === 'medium' ? 512 : 1024;
      // Step count matters more than resolution now that the march is
      // deterministic: with a per-pixel dither the extra steps were averaging
      // noise, without it they are buying actual shape.
      // Raised from 80. The march's quadrature error is a smooth function of
      // elevation (ds is (tOut-tIn)/steps and the jitter is indexed by step),
      // so it shows up as concentric rings around the zenith rather than as
      // noise — the review found them at (720,200) and (800,260). Halving the
      // step halves the error; the cost is one-off bake time, ~0.15 s per sun
      // change on this GPU, and the per-frame cost is unchanged.
      bakeUniforms.uCloudSteps.value = ctx.quality === 'low' ? 32 : ctx.quality === 'medium' ? 72 : 112;
      domeUniforms.uTexelAngle.value = (Math.PI / 2) / cubeSize;

      ensureLUT(true);
      captureReference();
      api.sunIntensity = derived.sunLux;

      cubeRT = new THREE.WebGLCubeRenderTarget(cubeSize, {
        type: THREE.HalfFloatType,
        format: THREE.RGBAFormat,
        generateMipmaps: true,
        minFilter: THREE.LinearMipmapLinearFilter,
        magFilter: THREE.LinearFilter,
        colorSpace: THREE.LinearSRGBColorSpace,
      });
      cubeRT.texture.name = 'skyCube';
      api.cubeTexture = cubeRT.texture;

      bakeMat = new THREE.ShaderMaterial({
        uniforms: bakeUniforms,
        vertexShader: SKY_VERT,
        fragmentShader: SKY_BAKE_FRAG,
        side: THREE.BackSide,
        depthWrite: false,
        depthTest: false,
        fog: false,
        toneMapped: false,
      });
      bakeScene = new THREE.Scene();
      const bakeMesh = new THREE.Mesh(new THREE.BoxGeometry(1, 1, 1), bakeMat);
      bakeMesh.frustumCulled = false;
      bakeScene.add(bakeMesh);

      cubeCamera = new THREE.CubeCamera(0.1, 10, cubeRT);

      bake(ctx.renderer);

      domeUniforms.uSkyCube.value = cubeRT.texture;
      sharedUniforms.aaaSkyCube.value = cubeRT.texture;

      domeMat = new THREE.ShaderMaterial({
        uniforms: domeUniforms,
        vertexShader: SKY_VERT,
        fragmentShader: SKY_DOME_FRAG,
        side: THREE.BackSide,
        depthWrite: false,
        depthTest: false,
        fog: false,
        toneMapped: false,
      });

      dome = new THREE.Mesh(new THREE.BoxGeometry(1, 1, 1), domeMat);
      dome.name = 'skyDome';
      dome.scale.setScalar(1200);
      dome.frustumCulled = false;
      dome.renderOrder = -10000;
      dome.castShadow = false;
      dome.receiveShadow = false;
      dome.userData.noCollide = true;
      dome.userData.aaaHelper = true;
      ctx.scene.add(dome);

      // USE_FOG must be on for the aerial-perspective chunk to compile in.
      // Density 0 so any material we did not register is unaffected.
      ctx.scene.fog = new THREE.FogExp2(0x000000, 0.0);

      updateFog();

      ctx.sky = api;
    },

    update(dt, ctx) {
      if (dome) dome.position.copy(ctx.camera.position);
      if (dirty) bake(ctx.renderer);
    },

    dispose() {
      dome?.removeFromParent();
      domeMat?.dispose();
      bakeMat?.dispose();
      cubeRT?.dispose();
    },
  };
}
